/**
 * Currency Formatter
 * 
 * Handles progressive formatting of currency and income values with comma separators,
 * decimal handling, and intelligent validation for financial contexts.
 */

import { BaseFormatter, FormatContext, ExtendedFormattingResult } from './base-formatter';
import { FormattingOptions, ValidationResult } from '../types/formatting-types';

/**
 * Currency formatting options
 */
interface CurrencyFormattingOptions extends FormattingOptions {
  /** Whether to include currency symbol ($) */
  includeCurrencySymbol?: boolean;
  /** Allow decimal places */
  allowDecimals?: boolean;
  /** Maximum decimal places */
  maxDecimalPlaces?: number;
  /** Minimum allowed value */
  minValue?: number;
  /** Maximum allowed value */
  maxValue?: number;
}

/**
 * Currency formatter implementing comma-separated thousands with optional decimals
 * 
 * Features:
 * - Progressive comma insertion (1,234,567)
 * - Optional decimal support (.99)
 * - Income validation ranges
 * - Real-time formatting during typing
 * - Support for large values (up to millions)
 */
export class CurrencyFormatter extends BaseFormatter {
  private readonly maxIntegerDigits = 10; // Up to $9,999,999,999
  private readonly defaultMaxDecimals = 2;

  constructor() {
    super('currency', {
      includeCurrencySymbol: false,
      allowDecimals: true,
      maxDecimalPlaces: 2,
      minValue: 0,
      maxValue: 9999999999 // ~10 billion max
    });
  }

  /**
   * Extract raw numeric value from formatted currency
   */
  public extractRaw(formattedValue: string): string {
    // Remove commas, dollar signs, and whitespace
    const cleaned = formattedValue.replace(/[$,\s]/g, '');
    
    // Keep only digits and decimal point
    return cleaned.replace(/[^\d.]/g, '');
  }

  /**
   * Check if this formatter handles currency fields
   */
  public canHandle(fieldType: string): boolean {
    return fieldType === 'currency' || 
           fieldType === 'money' || 
           fieldType === 'income' ||
           fieldType === 'housing_payment' ||
           fieldType === 'salary' ||
           fieldType === 'amount';
  }

  /**
   * Get the expected format pattern
   */
  public getFormatPattern(): string {
    return '1,234'; // With optional .99
  }

  /**
   * Sanitize input to only allow numeric characters and decimal point
   */
  protected sanitizeInput(value: string): string {
    // Remove everything except digits and decimal point
    let sanitized = value.replace(/[^\d.]/g, '');
    
    // Only allow one decimal point
    const decimalIndex = sanitized.indexOf('.');
    if (decimalIndex !== -1) {
      sanitized = sanitized.substring(0, decimalIndex + 1) + 
                 sanitized.substring(decimalIndex + 1).replace(/\./g, '');
    }
    
    return sanitized;
  }

  /**
   * Perform progressive currency formatting
   */
  protected performFormat(context: FormatContext): ExtendedFormattingResult {
    const { rawValue, cursorPosition, isProgressive, options } = context;
    const opts = options as CurrencyFormattingOptions;
    
    // Handle empty value
    if (!rawValue) {
      return {
        formattedValue: '',
        rawValue: '',
        success: true,
        errors: [],
        newCursorPosition: cursorPosition,
        wasModified: false
      };
    }

    // Split into integer and decimal parts
    const parts = rawValue.split('.');
    let integerPart = parts[0] || '';
    let decimalPart = parts[1] || '';

    // Limit integer part length
    if (integerPart.length > this.maxIntegerDigits) {
      integerPart = integerPart.substring(0, this.maxIntegerDigits);
    }

    // Limit decimal part
    const maxDecimals = opts?.maxDecimalPlaces ?? this.defaultMaxDecimals;
    if (decimalPart.length > maxDecimals) {
      decimalPart = decimalPart.substring(0, maxDecimals);
    }

    // Format integer part with commas
    const formattedInteger = this.addCommaThousands(integerPart);
    
    // Build formatted value
    let formattedValue = formattedInteger;
    
    // Add decimal part if present or if user is typing decimal
    if (parts.length > 1 || rawValue.includes('.')) {
      formattedValue += '.' + decimalPart;
    }

    // Calculate cursor position for progressive formatting
    let newCursorPosition = cursorPosition;
    if (isProgressive && cursorPosition !== undefined) {
      newCursorPosition = this.calculateCurrencyCursorPosition(
        rawValue,
        formattedValue,
        cursorPosition
      );
    }

    // Reconstruct raw value for consistency
    const reconstructedRaw = integerPart + (parts.length > 1 ? '.' + decimalPart : '');

    return {
      formattedValue,
      rawValue: reconstructedRaw,
      success: true,
      errors: [],
      newCursorPosition,
      wasModified: rawValue !== formattedValue
    };
  }

  /**
   * Validate currency value
   */
  protected performValidation(rawValue: string, options?: FormattingOptions): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const opts = options as CurrencyFormattingOptions;

    // Convert to numeric value for validation
    const numericValue = parseFloat(rawValue);
    
    if (isNaN(numericValue)) {
      errors.push('Please enter a valid amount');
      return { isValid: false, errors, warnings };
    }

    // Check minimum value
    const minValue = opts?.minValue ?? 0;
    if (numericValue < minValue) {
      errors.push(`Amount cannot be less than ${this.formatCurrencyForDisplay(minValue.toString())}`);
    }

    // Check maximum value
    const maxValue = opts?.maxValue ?? this.defaultOptions.maxValue!;
    if (numericValue > maxValue) {
      errors.push(`Amount cannot exceed ${this.formatCurrencyForDisplay(maxValue.toString())}`);
    }

    // Validate decimal places
    const decimalIndex = rawValue.indexOf('.');
    if (decimalIndex !== -1) {
      const decimalPart = rawValue.substring(decimalIndex + 1);
      const maxDecimals = opts?.maxDecimalPlaces ?? this.defaultMaxDecimals;
      
      if (decimalPart.length > maxDecimals) {
        errors.push(`Amount cannot have more than ${maxDecimals} decimal places`);
      }
    }

    // Context-specific warnings for income fields
    if (this.isIncomeField(rawValue, options)) {
      if (numericValue > 0 && numericValue < 1000) {
        warnings.push('Monthly income seems low - please verify');
      }
      
      if (numericValue > 50000) {
        warnings.push('Monthly income is very high - please verify');
      }
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Check if input represents a complete currency value
   */
  protected isCompleteInput(rawValue: string): boolean {
    // Complete if has at least one digit and no trailing decimal
    return /^\d+(\.\d+)?$/.test(rawValue) && rawValue.length > 0;
  }

  /**
   * Check if character is valid for currency input
   */
  protected isValidInputCharacter(char: string): boolean {
    return /[\d.]/.test(char);
  }

  /**
   * Remove formatting to get raw numeric value
   */
  protected stripFormatting(value: string): string {
    return value.replace(/[$,\s]/g, '');
  }

  /**
   * Add comma thousands separators to integer string
   */
  private addCommaThousands(integerString: string): string {
    if (!integerString) return '';
    
    // Add commas every 3 digits from the right
    return integerString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Calculate cursor position after currency formatting
   */
  private calculateCurrencyCursorPosition(
    rawValue: string,
    formattedValue: string,
    originalCursor: number
  ): number {
    // Count digits before cursor in raw value
    let digitsBeforeCursor = 0;
    for (let i = 0; i < Math.min(originalCursor, rawValue.length); i++) {
      if (/\d/.test(rawValue[i])) {
        digitsBeforeCursor++;
      }
    }

    // Find position in formatted value that corresponds to same digit count
    let digitCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitCount++;
        if (digitCount === digitsBeforeCursor) {
          return i + 1;
        }
      }
    }

    // Default: place cursor at end
    return formattedValue.length;
  }

  /**
   * Check if this is likely an income field based on context
   */
  private isIncomeField(value: string, options?: FormattingOptions): boolean {
    // This could be enhanced to check field names or types
    return false; // Placeholder for future context detection
  }

  /**
   * Format currency for display with proper commas
   */
  private formatCurrencyForDisplay(value: string): string {
    const result = this.format(value, {}, undefined, false);
    return result.formattedValue;
  }

  /**
   * Format currency value for display purposes
   * 
   * @param currencyValue Raw currency value
   * @returns Formatted currency string
   */
  public static formatCurrencyForDisplay(currencyValue: string): string {
    const formatter = new CurrencyFormatter();
    const result = formatter.format(currencyValue, {}, undefined, false);
    return result.formattedValue;
  }

  /**
   * Check if a string looks like a currency amount
   * 
   * @param value String to check
   * @returns Whether it looks like currency
   */
  public static looksLikeCurrency(value: string): boolean {
    const cleaned = value.replace(/[$,\s]/g, '');
    return /^\d+(\.\d{0,2})?$/.test(cleaned) && cleaned.length > 0;
  }

  /**
   * Parse currency from various formats
   * 
   * @param input Currency in any format
   * @returns Clean numeric string
   */
  public static parseCurrency(input: string): string {
    // Remove currency symbols, commas, and spaces
    let cleaned = input.replace(/[$,\s]/g, '');
    
    // Ensure proper decimal format
    const decimalIndex = cleaned.indexOf('.');
    if (decimalIndex !== -1) {
      const beforeDecimal = cleaned.substring(0, decimalIndex);
      const afterDecimal = cleaned.substring(decimalIndex + 1);
      cleaned = beforeDecimal + '.' + afterDecimal.substring(0, 2); // Max 2 decimal places
    }
    
    return cleaned;
  }

  /**
   * Convert currency to cents (for financial calculations)
   * 
   * @param currencyValue Currency value as string
   * @returns Value in cents as integer
   */
  public static toCents(currencyValue: string): number {
    const numeric = parseFloat(currencyValue.replace(/[$,\s]/g, ''));
    if (isNaN(numeric)) return 0;
    return Math.round(numeric * 100);
  }

  /**
   * Convert cents to currency string
   * 
   * @param cents Value in cents
   * @returns Formatted currency string
   */
  public static fromCents(cents: number): string {
    const dollars = cents / 100;
    return CurrencyFormatter.formatCurrencyForDisplay(dollars.toFixed(2));
  }

  /**
   * Validate if value is within reasonable income ranges
   * 
   * @param value Currency value to validate
   * @param fieldType Type of field (income, housing_payment, etc.)
   * @returns Whether value seems reasonable
   */
  public static isReasonableAmount(value: string, fieldType?: string): boolean {
    const numeric = parseFloat(value.replace(/[$,\s]/g, ''));
    if (isNaN(numeric)) return false;

    // Context-specific validation ranges
    switch (fieldType) {
      case 'income':
        return numeric >= 500 && numeric <= 50000; // Monthly income
      case 'housing_payment':
        return numeric >= 200 && numeric <= 10000; // Monthly housing
      default:
        return numeric >= 0 && numeric <= 999999; // General amounts
    }
  }
}